// Phase J1 - currency-aware money display + denominations.
//
// The shop carries a single immutable `currency` ISO-4217 code (e.g. "php").
// This module is the single client-side source of truth for how that currency
// renders (symbol, decimal places) and which denominations a cashier counts /
// makes change with - replacing the per-component hardcoded `₱` and
// `[1000..20]` note list. `setActiveCurrency(shop.currency)` is called once when
// the active shop is established; every money render then uses `money(amount)`
// and every denomination grid uses `currencyDenominations()`. Static reference
// data, so it lives on the client (available offline). A future server-config
// pass can feed this same shape without touching call sites.

export interface ICurrencyInfo {
  code: string; // ISO-4217 lowercased, e.g. "php"
  symbol: string; // "₱"
  decimals: number; // 2
  denominations: number[]; // notes + coins, high -> low
}

// Known currencies. Add new ones here (data-only) - no call site changes.
const CURRENCIES: Record<string, ICurrencyInfo> = {
  php: {
    code: 'php',
    symbol: '₱',
    decimals: 2,
    // Notes 1000-20 + coins 20-1 piso and 25 centavo. Sub-25c coins are
    // effectively out of use; revisit with a real operator (Phase J open Q).
    denominations: [1000, 500, 200, 100, 50, 20, 10, 5, 1, 0.25],
  },
};

// Resolve a currency code to its info. Unknown codes degrade gracefully: the
// ISO code (upper-cased) becomes the symbol and the denomination grid is empty
// (count UI falls back to a single total field) - never throws.
export function resolveCurrency(
  code: string | null | undefined
): ICurrencyInfo {
  const key = (code ?? '').trim().toLowerCase();
  return (
    CURRENCIES[key] ?? {
      code: key || 'unknown',
      symbol: key ? key.toUpperCase() + ' ' : '',
      decimals: 2,
      denominations: [],
    }
  );
}

let _active: ICurrencyInfo = CURRENCIES.php;

// Set when the active shop loads / switches. Idempotent.
export function setActiveCurrency(code: string | null | undefined): void {
  _active = resolveCurrency(code);
}

export function activeCurrency(): ICurrencyInfo {
  return _active;
}

// Format an amount in the active shop currency, e.g. money(123.5) -> "₱123.50".
export function money(amount: number): string {
  return `${_active.symbol}${amount.toFixed(_active.decimals)}`;
}

// The active currency's denominations (notes + coins, high -> low) for count /
// change grids. Empty for an unknown currency.
export function currencyDenominations(): number[] {
  return _active.denominations;
}
